/**
 * Article Content Injection Script - Week 4
 *
 * Handles dynamic content injection for:
 * 1. H2 heading IDs for TOC anchor links
 * 2. Inline author badges (after intro paragraphs)
 * 3. Inline product CTAs (at configurable positions)
 * 4. Visual content blocks (at specified placements)
 *
 * Progressive enhancement approach - gracefully degrades without JavaScript
 */

(function() {
    'use strict';
  
    /**
     * Convert text to URL-friendly ID
     */
    function slugify(text) {
      return text
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '')
        .replace(/[\s_-]+/g, '-')
        .replace(/^-+|-+$/g, '');
    }
  
    /**
     * Add IDs to H2 headings for TOC anchor links
     */
    function addHeadingIDs() {
      const articleContent = document.querySelector('.article__content');
      if (!articleContent) return;
  
      const headings = articleContent.querySelectorAll('h2');
  
      headings.forEach((heading, index) => {
        // Skip if heading already has an ID
        if (heading.id) return;
  
        // Generate ID from heading text
        const text = heading.textContent || heading.innerText;
        let id = slugify(text);
  
        // Ensure uniqueness (add index if duplicate)
        if (document.getElementById(id)) {
          id = `${id}-${index + 1}`;
        }
  
        heading.id = id;
  
        // Add scroll margin for better UX when jumping to section
        heading.style.scrollMarginTop = '100px';
      });
    }
  
    /**
     * Inject inline author badge after intro (paragraphs 2-3)
     */
    function injectAuthorBadge() {
      const articleContent = document.querySelector('.article__content');
      const authorBadgeData = document.querySelector('[data-author-badge-inline]');
  
      if (!articleContent || !authorBadgeData) return;
  
      // Find paragraphs in article content
      const paragraphs = articleContent.querySelectorAll('p');
  
      // Insert after 2nd or 3rd paragraph (whichever exists)
      let targetParagraph = paragraphs[2] || paragraphs[1];
  
      if (targetParagraph) {
        // Clone the author badge template
        const authorBadge = authorBadgeData.cloneNode(true);
        authorBadge.removeAttribute('data-author-badge-inline');
        authorBadge.style.display = 'flex'; // Un-hide
  
        // Insert after target paragraph
        targetParagraph.after(authorBadge);
      }
    }
  
    /**
     * Inject inline product CTAs at specified positions
     */
    function injectProductCards() {
      const articleContent = document.querySelector('.article__content');
      if (!articleContent) return;
  
      // Get product placement configuration from metafield
      const productDataElement = document.querySelector('[data-product-placements]');
      if (!productDataElement) return;
  
      try {
        const placements = JSON.parse(productDataElement.textContent);
        if (!Array.isArray(placements) || placements.length === 0) return;
  
        // Get all block-level elements (p, h2, h3, etc.)
        const blocks = articleContent.querySelectorAll('p, h2, h3, ul, ol, blockquote');
  
        placements.forEach(placement => {
          const { product_id, position, context } = placement;
  
          // Find target block based on position index
          const targetBlock = blocks[position - 1];
          if (!targetBlock) return;
  
          // Get product card template
          const productTemplate = document.querySelector(`[data-product-card="${product_id}"]`);
          if (!productTemplate) return;
  
          // Clone and insert
          const productCard = productTemplate.cloneNode(true);
          productCard.removeAttribute('data-product-card');
          productCard.style.display = 'block'; // Un-hide
  
          // Add context text if provided
          if (context) {
            const contextElement = productCard.querySelector('.product-card-inline__context');
            if (contextElement) {
              contextElement.textContent = context;
            }
          }
  
          // Insert after target block
          targetBlock.after(productCard);
        });
      } catch (error) {
        console.error('Error parsing product placements:', error);
      }
    }
  
    /**
     * Inject visual content blocks at specified placements
     */
    function injectVisualContent() {
      const articleContent = document.querySelector('.article__content');
      if (!articleContent) return;
  
      // Get visual content configuration from metafield
      const visualDataElement = document.querySelector('[data-visual-content]');
      if (!visualDataElement) return;
  
      try {
        const visualContent = JSON.parse(visualDataElement.textContent);
        if (!visualContent) return;
  
        // Get all block-level elements
        const blocks = articleContent.querySelectorAll('p, h2, h3, ul, ol, blockquote');
  
        // Helper to insert content at placement position
        function insertAtPlacement(templateSelector, placementKey) {
          const items = visualContent[placementKey];
          if (!Array.isArray(items)) return;
  
          items.forEach(item => {
            const { placement } = item;
            let targetBlock;
  
            // Determine target position
            switch (placement) {
              case 'after_intro':
                targetBlock = blocks[2] || blocks[1];
                break;
              case 'mid_article':
                targetBlock = blocks[Math.floor(blocks.length / 2)];
                break;
              case 'before_conclusion':
                targetBlock = blocks[blocks.length - 3] || blocks[blocks.length - 1];
                break;
              default:
                // Numeric position
                if (typeof placement === 'number') {
                  targetBlock = blocks[placement - 1];
                }
            }
  
            if (!targetBlock) return;
  
            // Find template
            const template = document.querySelector(`${templateSelector}[data-id="${item.id}"]`);
            if (!template) return;
  
            // Clone and insert
            const visualBlock = template.cloneNode(true);
            visualBlock.style.display = 'block'; // Un-hide
            targetBlock.after(visualBlock);
          });
        }
  
        // Insert each visual content type
        insertAtPlacement('[data-infographic]', 'infographics');
        insertAtPlacement('[data-before-after]', 'before_after_comparisons');
        insertAtPlacement('[data-gallery]', 'product_galleries');
        insertAtPlacement('[data-diagram]', 'diagrams');
        insertAtPlacement('[data-video]', 'video_embeds');
        insertAtPlacement('[data-quote]', 'pull_quotes');
        insertAtPlacement('[data-side-by-side]', 'side_by_side_comparisons');
      } catch (error) {
        console.error('Error parsing visual content:', error);
      }
    }
  
    /**
     * Track product clicks for analytics
     */
    function trackProductClicks() {
      const productCards = document.querySelectorAll('.product-card-inline');
  
      productCards.forEach(card => {
        const productId = card.getAttribute('data-product-id');
        const position = card.getAttribute('data-position');
  
        // Track "Add to Cart" clicks
        const addToCartBtn = card.querySelector('.product-card-inline__cta');
        if (addToCartBtn) {
          addToCartBtn.addEventListener('click', () => {
            // Google Analytics 4 event
            if (typeof gtag === 'function') {
              gtag('event', 'add_to_cart_from_article', {
                product_id: productId,
                position: position,
                article_id: window.location.pathname
              });
            }
  
            // Facebook Pixel event
            if (typeof fbq === 'function') {
              fbq('track', 'AddToCart', {
                content_ids: [productId],
                content_type: 'product',
                source: 'blog_article'
              });
            }
          });
        }
  
        // Track product view (impression)
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              if (typeof gtag === 'function') {
                gtag('event', 'view_item', {
                  product_id: productId,
                  position: position,
                  source: 'article_inline'
                });
              }
              observer.unobserve(entry.target);
            }
          });
        }, { threshold: 0.5 });
  
        observer.observe(card);
      });
    }

    function normalizeLexiconSlug(value) {
      if (typeof value !== 'string') return '';

      try {
        return decodeURIComponent(value).trim().replace(/^\/+|\/+$/g, '').toLowerCase();
      } catch (error) {
        return value.trim().replace(/^\/+|\/+$/g, '').toLowerCase();
      }
    }

    function getLexiconSlugFromHref(href) {
      if (typeof href !== 'string' || href === '') return '';

      const cleanHref = href.split('#')[0].split('?')[0];
      const glossaryPageMatch = cleanHref.match(/\/pages\/glossary-([^/]+)$/);
      if (glossaryPageMatch) return glossaryPageMatch[1];

      const nestedGlossaryMatch = cleanHref.match(/\/pages\/glossary\/([^/]+)$/);
      if (nestedGlossaryMatch) return nestedGlossaryMatch[1];

      return '';
    }

    function createLexiconDefinitionMap(entries) {
      const definitions = new Map();
      if (!Array.isArray(entries)) return definitions;

      entries.forEach(entry => {
        const slug = normalizeLexiconSlug(entry && entry.slug);
        const definition = String(
          (entry && (entry.short_definition || entry.shortDefinition || entry.definition)) || ''
        ).trim();

        if (!slug || !definition) return;

        definitions.set(slug, {
          slug,
          term: String((entry && entry.term) || slug).trim() || slug,
          definition,
        });
      });

      return definitions;
    }

    function getLexiconEntryForLink(link, definitions) {
      if (!link || !definitions) return null;

      const dataSlug = normalizeLexiconSlug(link.getAttribute('data-slug') || '');
      if (dataSlug && definitions.has(dataSlug)) return definitions.get(dataSlug);

      const hrefSlug = normalizeLexiconSlug(getLexiconSlugFromHref(link.getAttribute('href') || ''));
      if (hrefSlug && definitions.has(hrefSlug)) return definitions.get(hrefSlug);

      return null;
    }

    function shouldInterceptLexiconClick({ usesTouchTooltip, isActiveLink }) {
      return Boolean(usesTouchTooltip && !isActiveLink);
    }

    function readLexiconEntries() {
      const dataElement = document.getElementById('article-lexicon-data');
      if (!dataElement) return [];

      try {
        return JSON.parse(dataElement.textContent || '[]');
      } catch (error) {
        console.error('Error parsing article lexicon data:', error);
        return [];
      }
    }

    function usesTouchTooltip() {
      return Boolean(window.matchMedia && window.matchMedia('(hover: none), (pointer: coarse)').matches);
    }

    function renderLexiconTooltip(tooltip, entry) {
      tooltip.textContent = '';
      tooltip.setAttribute('role', 'tooltip');

      const term = document.createElement('strong');
      term.className = 'article-lexicon-tooltip__term';
      term.textContent = entry.term;

      const definition = document.createElement('p');
      definition.className = 'article-lexicon-tooltip__definition';
      definition.textContent = entry.definition;

      tooltip.append(term, definition);
    }

    function getLexiconTooltipPosition({ linkRect, tooltipRect, viewportWidth, usesTouchTooltip }) {
      const spacing = usesTouchTooltip ? 8 : 10;
      const viewportPadding = 12;
      const top = usesTouchTooltip
        ? linkRect.bottom + spacing
        : Math.max(viewportPadding, linkRect.top - tooltipRect.height - spacing);
      const centeredLeft = linkRect.left + (linkRect.width / 2) - (tooltipRect.width / 2);
      const maxLeft = viewportWidth - tooltipRect.width - viewportPadding;
      const left = Math.min(Math.max(centeredLeft, viewportPadding), Math.max(maxLeft, viewportPadding));

      return {
        top: Math.round(top),
        left: Math.round(left),
      };
    }

    function positionLexiconTooltip(tooltip, link) {
      const position = getLexiconTooltipPosition({
        linkRect: link.getBoundingClientRect(),
        tooltipRect: tooltip.getBoundingClientRect(),
        viewportWidth: window.innerWidth,
        usesTouchTooltip: usesTouchTooltip(),
      });

      tooltip.style.top = `${position.top}px`;
      tooltip.style.left = `${position.left}px`;
    }

    function initLexiconTooltips() {
      const articleContent = document.querySelector('.article__content');
      const tooltip = document.getElementById('article-lexicon-tooltip');
      if (!articleContent || !tooltip) return;

      const definitions = createLexiconDefinitionMap(readLexiconEntries());
      if (definitions.size === 0) return;

      const links = articleContent.querySelectorAll('a.lexicon-term');
      if (links.length === 0) return;

      let activeLink = null;

      function hideTooltip() {
        if (activeLink) {
          activeLink.classList.remove('is-lexicon-tooltip-open');
          activeLink.removeAttribute('aria-describedby');
        }

        tooltip.hidden = true;
        activeLink = null;
      }

      function showTooltip(link, entry) {
        if (activeLink && activeLink !== link) {
          activeLink.classList.remove('is-lexicon-tooltip-open');
          activeLink.removeAttribute('aria-describedby');
        }

        activeLink = link;
        renderLexiconTooltip(tooltip, entry);
        tooltip.hidden = false;
        link.classList.add('is-lexicon-tooltip-open');
        link.setAttribute('aria-describedby', tooltip.id);
        positionLexiconTooltip(tooltip, link);
      }

      links.forEach(link => {
        const entry = getLexiconEntryForLink(link, definitions);
        if (!entry) return;

        link.addEventListener('mouseenter', () => {
          if (!usesTouchTooltip()) showTooltip(link, entry);
        });

        link.addEventListener('mouseleave', () => {
          if (!usesTouchTooltip()) hideTooltip();
        });

        link.addEventListener('focus', () => {
          if (!usesTouchTooltip()) showTooltip(link, entry);
        });

        link.addEventListener('blur', () => {
          if (!usesTouchTooltip()) hideTooltip();
        });

        link.addEventListener('click', event => {
          const isActiveLink = activeLink === link && !tooltip.hidden;

          if (!shouldInterceptLexiconClick({
            usesTouchTooltip: usesTouchTooltip(),
            isActiveLink,
          })) {
            return;
          }

          event.preventDefault();
          showTooltip(link, entry);
        });
      });

      document.addEventListener('click', event => {
        if (!activeLink) return;
        const clickedLexiconLink = event.target.closest && event.target.closest('a.lexicon-term');
        if (clickedLexiconLink === activeLink || tooltip.contains(event.target)) return;
        hideTooltip();
      });

      document.addEventListener('keydown', event => {
        if (event.key === 'Escape') hideTooltip();
      });

      window.addEventListener('scroll', hideTooltip, { passive: true });
      window.addEventListener('resize', () => {
        if (!activeLink || usesTouchTooltip()) return;
        positionLexiconTooltip(tooltip, activeLink);
      }, { passive: true });
    }

    if (window.__SKINMART_TEST__) {
      window.__SKINMART_ARTICLE_LEXICON_TEST__ = {
        createLexiconDefinitionMap,
        getLexiconEntryForLink,
        getLexiconTooltipPosition,
        shouldInterceptLexiconClick,
      };
    }
  
    /**
     * Initialize all content injection
     */
    function init() {
      // Run immediately
      addHeadingIDs();
  
      // Run after DOM is fully loaded
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          injectAuthorBadge();
          injectProductCards();
          injectVisualContent();
          initLexiconTooltips();
          trackProductClicks();
        });
      } else {
        injectAuthorBadge();
        injectProductCards();
        injectVisualContent();
        initLexiconTooltips();
        trackProductClicks();
      }
    }
  
    // Run initialization
    init();
  })();
  